import React from "react";
import {
  AbsoluteFill,
  Img,
  Audio,
  Sequence,
  staticFile,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  Easing,
  random,
} from "remotion";

/**
 * PaperRipCollage — Trimbakeshwar test (17s @ 30fps, 1920x1080)
 *
 * Builds a torn-paper moodboard in real time: 34 anchor images, each clipped
 * by one of the pack's part_*.png masks, dropped onto a dark canvas with
 * rotation + scale jitter + tape/frame accents. Density curves with the
 * suspense beat. Final title card rips in last.
 */

const FPS = 30;
const W = 1920;
const H = 1080;
const TOTAL = FPS * 17; // 510 frames

// ─── Anchor images (locally staged from R2) ─────────────────────────────────
const ANCHOR_COUNT = 34;
const ANCHORS: string[] = Array.from({ length: ANCHOR_COUNT }, (_, i) =>
  staticFile(`trimbak/img_${i.toString().padStart(2, "0")}.jpg`),
);

// ─── Paper-rip asset paths ───────────────────────────────────────────────────
const PARTS = [1, 2, 3, 4, 5].map((n) => staticFile(`paper_rip/part_${n}.png`));
const TAPES = [1, 2, 3, 4, 5, 6, 7].map((n) =>
  staticFile(`paper_rip/tape_${n}.png`),
);
const FRAMES = [1, 2, 3, 4].map((n) => staticFile(`paper_rip/frame_${n}.png`));
// Part aspect ratios (w/h) so we can size the bounding box correctly
const PART_ASPECTS = [0.432, 0.49, 0.329, 0.293, 0.216];
const TAPE_ASPECTS = [0.138, 0.21, 0.194, 0.18, 0.436, 0.905, 0.239];

// Rip SFX – use the "Edge Fast" variants for quick punchy hits
const SFX_FAST = Array.from({ length: 10 }, (_, i) =>
  staticFile(`paper_rip/sfx/${(i + 1).toString().padStart(2, "0")} -- Edge Fast.wav`),
);

// ─── Piece schedule (deterministic) ──────────────────────────────────────────
// Density curve: slow start (0–3s), dense middle (3–13s), tail (13–15s),
// then title card at 15s.
function buildSchedule(): number[] {
  const sched: number[] = [];
  // 5 pieces in first 3s (frames 12 → 84, every 18 frames)
  for (let i = 0; i < 5; i++) sched.push(12 + i * 18);
  // 25 pieces in 3–13s (frames 90 → 378, every 12 frames)
  for (let i = 0; i < 25; i++) sched.push(90 + i * 12);
  // 4 pieces in 13–15s
  for (let i = 0; i < 4; i++) sched.push(390 + i * 15);
  return sched;
}
const SCHEDULE = buildSchedule(); // length 34, matches ANCHOR_COUNT

// ─── Scatter-with-bias layout ────────────────────────────────────────────────
// Divide canvas into a 6x4 grid (24 cells). For each piece, pick the
// least-occupied cell, then jitter within it. Result: visually balanced
// moodboard, no empty corners.
interface PieceCfg {
  src: string;
  partIdx: number; // 0..4
  cx: number; // center x px
  cy: number; // center y px
  scale: number; // 0.35..0.6 of min(W,H)
  rot: number; // deg
  flipH: boolean;
  startFrame: number;
  zIndex: number;
  accent: { kind: "tape" | "frame" | null; idx: number; rot: number; ox: number; oy: number };
}

function buildPieces(): PieceCfg[] {
  const COLS = 6;
  const ROWS = 4;
  const CELL_W = W / COLS;
  const CELL_H = H / ROWS;
  const counts: number[] = new Array(COLS * ROWS).fill(0);

  const out: PieceCfg[] = [];

  for (let i = 0; i < ANCHOR_COUNT; i++) {
    // Find the least-occupied cell (tie-break with deterministic jitter)
    let bestIdx = 0;
    let bestScore = Infinity;
    for (let c = 0; c < counts.length; c++) {
      const noise = random(`cell-${i}-${c}`) * 0.4;
      const score = counts[c] + noise;
      if (score < bestScore) {
        bestScore = score;
        bestIdx = c;
      }
    }
    counts[bestIdx] += 1;

    const col = bestIdx % COLS;
    const rowI = Math.floor(bestIdx / COLS);
    // Jitter inside the cell (keep some margin to canvas edges)
    const jx = (random(`jx-${i}`) - 0.5) * CELL_W * 0.7;
    const jy = (random(`jy-${i}`) - 0.5) * CELL_H * 0.7;
    const cx = (col + 0.5) * CELL_W + jx;
    const cy = (rowI + 0.5) * CELL_H + jy;

    const partIdx = Math.floor(random(`part-${i}`) * 5);
    const scale = 0.42 + random(`sc-${i}`) * 0.22; // 0.42..0.64
    const rot = (random(`rot-${i}`) - 0.5) * 26; // -13..+13 deg
    const flipH = random(`flip-${i}`) > 0.55;

    // ~25% chance of tape, ~12% chance of polaroid frame
    const accentRoll = random(`acc-${i}`);
    let accent: PieceCfg["accent"] = { kind: null, idx: 0, rot: 0, ox: 0, oy: 0 };
    if (accentRoll < 0.12) {
      accent = {
        kind: "frame",
        idx: Math.floor(random(`fidx-${i}`) * FRAMES.length),
        rot: (random(`frot-${i}`) - 0.5) * 10,
        ox: 0,
        oy: 0,
      };
    } else if (accentRoll < 0.37) {
      accent = {
        kind: "tape",
        idx: Math.floor(random(`tidx-${i}`) * TAPES.length),
        rot: (random(`trot-${i}`) - 0.5) * 60,
        ox: (random(`tox-${i}`) - 0.5) * 0.5,
        oy: -0.4 + random(`toy-${i}`) * 0.8,
      };
    }

    out.push({
      src: ANCHORS[i],
      partIdx,
      cx,
      cy,
      scale,
      rot,
      flipH,
      startFrame: SCHEDULE[i] ?? 480,
      zIndex: 100 + i, // newer on top
      accent,
    });
  }
  return out;
}

const PIECES = buildPieces();

// ─── Single torn piece ───────────────────────────────────────────────────────
const Piece: React.FC<{ cfg: PieceCfg }> = ({ cfg }) => {
  const frame = useCurrentFrame();
  const local = frame - cfg.startFrame;
  if (local < 0) return null;

  // Entrance: scale 0.55 → 1.08 → 1.0 (overshoot), opacity 0 → 1, 9 frames (0.3s)
  const t = Math.min(local, 9);
  const entranceScale = interpolate(t, [0, 5, 9], [0.55, 1.08, 1.0], {
    extrapolateRight: "clamp",
    easing: Easing.out(Easing.cubic),
  });
  const entranceOp = interpolate(t, [0, 4], [0, 1], {
    extrapolateRight: "clamp",
  });
  // Subtle settle wobble after landing
  const wobble = local > 9
    ? Math.sin((local - 9) * 0.4) * Math.max(0, 1 - (local - 9) / 12) * 0.6
    : 0;

  const partAspect = PART_ASPECTS[cfg.partIdx];
  // Base piece height in px = scale * min(W,H). Width = height * aspect.
  const pieceH = cfg.scale * Math.min(W, H);
  const pieceW = pieceH * partAspect;

  const partUrl = PARTS[cfg.partIdx];

  return (
    <div
      style={{
        position: "absolute",
        left: cfg.cx - pieceW / 2,
        top: cfg.cy - pieceH / 2,
        width: pieceW,
        height: pieceH,
        transform: `rotate(${cfg.rot + wobble}deg) scale(${entranceScale}) ${cfg.flipH ? "scaleX(-1)" : ""}`,
        opacity: entranceOp,
        zIndex: cfg.zIndex,
        filter: "drop-shadow(0 14px 22px rgba(0,0,0,0.7)) drop-shadow(0 4px 6px rgba(0,0,0,0.5))",
      }}
    >
      {/* Slight paper-tint backing behind torn shape for depth */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          background: "#f6ecd5",
          WebkitMaskImage: `url(${partUrl})`,
          maskImage: `url(${partUrl})`,
          WebkitMaskRepeat: "no-repeat",
          maskRepeat: "no-repeat",
          WebkitMaskSize: "100% 100%",
          maskSize: "100% 100%",
        }}
      />
      {/* The clipped anchor image */}
      <Img
        src={cfg.src}
        style={{
          position: "absolute",
          inset: "3% 3% 3% 3%",
          width: "94%",
          height: "94%",
          objectFit: "cover",
          WebkitMaskImage: `url(${partUrl})`,
          maskImage: `url(${partUrl})`,
          WebkitMaskRepeat: "no-repeat",
          maskRepeat: "no-repeat",
          WebkitMaskSize: "100% 100%",
          maskSize: "100% 100%",
        }}
      />
      {/* Optional accent */}
      {cfg.accent.kind === "tape" && (
        <Img
          src={TAPES[cfg.accent.idx]}
          style={{
            position: "absolute",
            top: `${50 + cfg.accent.oy * 50}%`,
            left: `${50 + cfg.accent.ox * 50}%`,
            width: pieceW * 0.55,
            height: pieceW * 0.55 / TAPE_ASPECTS[cfg.accent.idx],
            transform: `translate(-50%, -50%) rotate(${cfg.accent.rot}deg)`,
            opacity: 0.92,
            filter: "drop-shadow(0 3px 6px rgba(0,0,0,0.4))",
          }}
        />
      )}
      {cfg.accent.kind === "frame" && (
        <Img
          src={FRAMES[cfg.accent.idx]}
          style={{
            position: "absolute",
            inset: "-8%",
            width: "116%",
            height: "116%",
            objectFit: "contain",
            transform: `rotate(${cfg.accent.rot}deg)`,
            mixBlendMode: "multiply",
            opacity: 0.55,
            pointerEvents: "none",
          }}
        />
      )}
    </div>
  );
};

// ─── Title card (rips in last) ──────────────────────────────────────────────
const TitleCard: React.FC = () => {
  const frame = useCurrentFrame();
  const start = 450; // frame 15s
  const local = frame - start;
  if (local < 0) return null;
  const op = interpolate(local, [0, 6, 50, 60], [0, 1, 1, 0.95], {
    extrapolateRight: "clamp",
  });
  const sc = interpolate(local, [0, 8], [1.18, 1.0], {
    extrapolateRight: "clamp",
    easing: Easing.out(Easing.cubic),
  });
  const skew = interpolate(local, [0, 8], [-4, 0], {
    extrapolateRight: "clamp",
  });
  return (
    <AbsoluteFill
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 9999,
        pointerEvents: "none",
      }}
    >
      <div
        style={{
          position: "relative",
          padding: "44px 88px",
          background: "rgba(15,9,5,0.78)",
          border: "3px solid rgba(255,210,140,0.42)",
          borderRadius: 6,
          transform: `scale(${sc}) skewX(${skew}deg)`,
          opacity: op,
          boxShadow: "0 30px 80px rgba(0,0,0,0.85), 0 0 90px rgba(255,150,60,0.25)",
        }}
      >
        <div
          style={{
            fontFamily: "'Tiro Devanagari Hindi', 'Noto Serif Devanagari', serif",
            fontSize: 132,
            fontWeight: 900,
            color: "#ffe4b3",
            letterSpacing: 3,
            textShadow: "0 5px 30px rgba(0,0,0,0.95), 0 0 60px rgba(255,170,80,0.5)",
            lineHeight: 1.0,
          }}
        >
          त्र्यंबकेश्वर
        </div>
        <div
          style={{
            fontFamily: "Helvetica, Arial, sans-serif",
            fontSize: 30,
            fontWeight: 700,
            letterSpacing: 8,
            color: "rgba(255,224,180,0.78)",
            textAlign: "center",
            marginTop: 14,
          }}
        >
          THE  TWELFTH  JYOTIRLINGA
        </div>
      </div>
    </AbsoluteFill>
  );
};

// ─── Rip SFX layer (one per piece, picks from 10 fast variants) ─────────────
const RipSfxStack: React.FC = () => (
  <>
    {PIECES.map((p, i) => (
      <Sequence
        key={`sfx-${i}`}
        from={p.startFrame}
        durationInFrames={20}
      >
        <Audio
          src={SFX_FAST[i % SFX_FAST.length]}
          volume={0.55}
        />
      </Sequence>
    ))}
  </>
);

// ─── End fade ────────────────────────────────────────────────────────────────
const EndFade: React.FC = () => {
  const f = useCurrentFrame();
  const opacity = interpolate(f, [TOTAL - 20, TOTAL], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  if (opacity <= 0) return null;
  return (
    <AbsoluteFill
      style={{
        background: "#000",
        opacity,
        pointerEvents: "none",
        zIndex: 99999,
      }}
    />
  );
};

// ─── Main composition ────────────────────────────────────────────────────────
export const PaperRipCollage: React.FC = () => {
  return (
    <AbsoluteFill
      style={{
        // Dark warm chocolate paper-board background
        background:
          "radial-gradient(ellipse at center, #1a0f08 0%, #0a0604 70%, #050302 100%)",
      }}
    >
      {/* Subtle paper-grain texture using repeating gradients */}
      <AbsoluteFill
        style={{
          background:
            "repeating-linear-gradient(45deg, rgba(255,220,170,0.012) 0 2px, transparent 2px 5px), repeating-linear-gradient(-45deg, rgba(255,220,170,0.012) 0 2px, transparent 2px 5px)",
          mixBlendMode: "overlay",
          pointerEvents: "none",
        }}
      />

      {/* Suspense music bed */}
      <Audio src={staticFile("suspense_intro_17s.mp3")} />

      {/* All the rip SFX */}
      <RipSfxStack />

      {/* The collage pieces themselves */}
      {PIECES.map((cfg, i) => (
        <Piece key={i} cfg={cfg} />
      ))}

      {/* Vignette over everything */}
      <AbsoluteFill
        style={{
          background:
            "radial-gradient(ellipse at center, transparent 45%, rgba(0,0,0,0.55) 100%)",
          pointerEvents: "none",
          zIndex: 5000,
        }}
      />

      <TitleCard />
      <EndFade />
    </AbsoluteFill>
  );
};

export default PaperRipCollage;

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
